import { useTranslation } from "react-i18next";
import { moveSlots, removeSlots } from "@heraion/shared";

import type { ProgramEditorV3Props } from "./types";
import type { DaySelection } from "./useDaySelection";
import type { V3ExerciseActions } from "./useV3ExerciseActions";
import type { V3Navigation } from "./useV3Navigation";

type Args = Pick<
  ProgramEditorV3Props,
  "draft" | "setDraft" | "confirm" | "showToast" | "onUndo"
> & { nav: V3Navigation; selection: DaySelection; exercises: V3ExerciseActions };

// The selection bar's writes (#336, 2B): move the selected run up or down,
// copy it to other days, remove it. One setDraft each, so one Undo each.
// The rule for the selection is the «Rule» sheet (slice 5, ProgramEditorV3).
export function useV3SelectionActions({
  draft,
  setDraft,
  confirm,
  showToast,
  onUndo,
  nav,
  selection,
  exercises,
}: Args) {
  const { t } = useTranslation();
  const sIdx = nav.dayIdx;
  const ids = selection.selected;

  // Only one run moves: moveSlots refuses the rest, and the arrow says so.
  const canMove = (delta: -1 | 1) => ids.length > 0 && moveSlots(draft, sIdx, ids, delta) !== draft;
  const move = (delta: -1 | 1) => setDraft((d) => moveSlots(d, sIdx, ids, delta));

  const copyToDays = (days: number[]) => {
    exercises.copyToDays(ids, days);
    selection.clear();
  };

  // Same rule as the editor's other removals: ask, then Undo in the toast.
  const remove = () => {
    const count = ids.length;
    if (count === 0) return;
    const gone = [...ids];
    confirm({
      title: t("editorV3.removeSelectedTitle", { count, defaultValue: "Remove {{count}} exercises?" }),
      body: t("editorV3.removeSelectedBody", { count, defaultValue: "They leave this day in every round." }),
      confirmLabel: t("editorV3.remove", { defaultValue: "Remove" }),
      cancelLabel: t("program.cancel", { defaultValue: "Cancel" }),
    }).then((yes) => {
      if (!yes) return;
      setDraft((d) => removeSlots(d, sIdx, gone));
      selection.clear();
      showToast(
        t("editorV3.selectedRemoved", { count, defaultValue: "{{count}} exercises removed" }),
        { label: t("program.undo", { defaultValue: "Undo" }), onPress: onUndo },
      );
    });
  };

  return { canMove, move, copyToDays, remove };
}

export type V3SelectionActions = ReturnType<typeof useV3SelectionActions>;
